import { useCallback, useRef } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';

import type { Permission, Permissions } from '../../../../../reducers/root.reducer';
import { PortalFeatureFlags } from '../../../../../services/featureFlags/portalFeatureFlags';
import { useBooleanFeatureFlagResolution } from '../../../../../services/featureFlags/useBooleanFeatureFlagResolution';

/**
 * Who may do something: one permission, any of a list, or a list per permission system, picked by
 * the `backend_auth_useAuthZForGetApps` flag (AuthZ resource permissions such as `ViewBrand`, or
 * the legacy roles such as `Owner`).
 */
export type AccessRule = Permission | Permissions | { legacy: Permissions; authZ: Permissions };

const allowedFor = (rule: AccessRule, useAuthZPermissions: boolean): Permissions => {
  if (typeof rule === 'string') return [rule];
  if (Array.isArray(rule)) return rule;
  return useAuthZPermissions ? rule.authZ : rule.legacy;
};

/**
 * The user's permissions and whether they allow something.
 *
 * `hasAccess(rule)` is true when the user holds any permission the rule allows. A rule is a
 * permission, a list, or a `{ legacy, authZ }` pair, where the `backend_auth_useAuthZForGetApps`
 * flag picks the list (pending counts as legacy, as everywhere else).
 *
 * `permissions` is `state.permissions` as of its last load. The store empties between leaving one
 * brand and loading the next (`clearPermissions` in `selectedAppChanged`), so the last loaded set is
 * held through that gap and answers stay stable across a brand switch.
 *
 * @example
 * const { hasAccess, isFlipdishStaff } = usePermissions();
 * hasAccess('Owner');
 * hasAccess(['Owner', 'ManagedOwner']);
 * hasAccess({ legacy: ['Owner'], authZ: ['ViewBrand'] });
 */
export const usePermissions = () => {
  const statePermissions = useAppSelector((state) => state.permissions);
  const lastLoadedRef = useRef<Permissions>(statePermissions ?? []);
  if (statePermissions?.length) lastLoadedRef.current = statePermissions;
  const permissions = lastLoadedRef.current;

  const useAuthZForGetApps = useBooleanFeatureFlagResolution(
    PortalFeatureFlags.backend_auth_useAuthZForGetApps,
    false
  );
  const useAuthZPermissions = useAuthZForGetApps.status === 'resolved' && useAuthZForGetApps.value;

  const hasAccess = useCallback(
    (rule: AccessRule) =>
      allowedFor(rule, useAuthZPermissions).some((permission) => permissions.includes(permission)),
    [permissions, useAuthZPermissions]
  );

  return {
    /** The last loaded `state.permissions`. */
    permissions,
    /** Whether AuthZ permissions apply rather than legacy roles. */
    useAuthZPermissions,
    /** Whether the user holds any permission the rule allows. */
    hasAccess,
    /** Whether the user is a Flipdish Staff member. */
    isFlipdishStaff: permissions.includes('FlipdishStaff'),
  };
};
